import { createAsyncThunk } from '@reduxjs/toolkit'
import { membershipServices } from '../../services/Graphql'
import { OTP_EXPIRY_TIME } from '@common'
import { recordResendAttempt } from '../../utils/otpResendLimiter'

/**
 * Register new member with email and password
 * @param {Object} input - register data
 * @param {string} input.name - member name
 * @param {string} input.email - email address
 * @param {string} input.password - password
 * @param {string} input.phone - phone number (optional)
 */
export const registerMemberAsync = createAsyncThunk('membership/registerMember', async (input, thunk) => {
    try {
        const result = await membershipServices.registerMember(input)
        if (!result.success) {
            return thunk.rejectWithValue(result.error)
        }

        // Registration successful, OTP will be automatically sent to email
        // Record OTP request for rate limiting
        recordResendAttempt(input.email)

        // Set OTP expiry time
        const otpExpiry = Date.now() + OTP_EXPIRY_TIME * 1000

        return {
            ...result,
            email: input.email,
            otpExpiry,
        }
    } catch (error) {
        return thunk.rejectWithValue(error.message)
    }
})

/**
 * Require OTP for email verification (resend)
 * @param {Object} input - OTP request data
 * @param {string} input.email - email address
 */
export const requireOTPAsync = createAsyncThunk('membership/requireOTP', async (input, thunk) => {
    try {
        const result = await membershipServices.requireOTP(input)
        if (!result.success) {
            // Even when failed, we should pass nextAvailableTime if available
            return thunk.rejectWithValue({
                error: result.error,
                nextAvailableTime: result.nextAvailableTime || null,
            })
        }

        // OTP will be automatically sent to email
        // Record OTP request for rate limiting
        recordResendAttempt(input.email)

        // Set OTP expiry time
        const otpExpiry = Date.now() + OTP_EXPIRY_TIME * 1000

        return {
            ...result,
            email: input.email,
            otpExpiry,
            nextAvailableTime: result.nextAvailableTime || null,
        }
    } catch (error) {
        return thunk.rejectWithValue({
            error: error.message,
            nextAvailableTime: null,
        })
    }
})

/**
 * Verify email OTP
 * @param {Object} input - verification data
 * @param {string} input.email - email address
 * @param {string} input.otp - 6-digit OTP code
 */
export const verifyEmailOTPAsync = createAsyncThunk('membership/verifyEmailOTP', async (input, thunk) => {
    try {
        const result = await membershipServices.verifyEmailOTP(input)
        if (!result.success) {
            return thunk.rejectWithValue(result.error)
        }

        return {
            ...result,
            email: input.email,
        }
    } catch (error) {
        return thunk.rejectWithValue(error.message)
    }
})

/**
 * Get member access token (login)
 * @param {Object} input - access token request data
 * @param {string} input.email - email address
 * @param {string} input.password - password
 * @param {boolean} input.isRememberMe - whether to remember me
 */
export const getMemberAccessTokenAsync = createAsyncThunk('membership/getMemberAccessToken', async (input, thunk) => {
    try {
        const result = await membershipServices.getMemberAccessToken(input)
        if (!result.success) {
            // Reject with error and errorCode for proper error handling
            return thunk.rejectWithValue({
                error: result.error,
                errorCode: result.errorCode,
            })
        }

        // Save tokens to localStorage
        if (result.accessToken) {
            membershipServices.saveTokens(result.accessToken, result.refreshToken, result.expiresAt)
        }

        return result
    } catch (error) {
        return thunk.rejectWithValue({
            error: error.message,
            errorCode: null,
        })
    }
})

/**
 * Refresh access token using refresh token
 * @param {Object} input - refresh token request data
 * @param {string} input.refreshToken - refresh token
 */
export const refreshAccessTokenAsync = createAsyncThunk('membership/refreshAccessToken', async (input, thunk) => {
    try {
        const result = await membershipServices.refreshAccessToken(input)
        if (!result.success) {
            // Reject with error and errorCode for proper error handling
            return thunk.rejectWithValue({
                error: result.error,
                errorCode: result.errorCode,
            })
        }

        // Save new tokens to localStorage (token rotation)
        if (result.accessToken && result.refreshToken) {
            membershipServices.saveTokens(result.accessToken, result.refreshToken, result.expiresAt)
        }

        return result
    } catch (error) {
        return thunk.rejectWithValue({
            error: error.message,
            errorCode: null,
        })
    }
})

/**
 * Revoke refresh token (logout)
 * @param {Object} input - revoke token request data
 * @param {string} input.refreshToken - refresh token to revoke
 */
export const revokeRefreshTokenAsync = createAsyncThunk('membership/revokeRefreshToken', async (input, thunk) => {
    try {
        const result = await membershipServices.revokeRefreshToken(input)

        // Clear tokens from localStorage even if revoke fails
        // (token might already be revoked or invalid)
        membershipServices.clearTokens()

        if (!result.success) {
            // Reject with error and errorCode, but tokens are already cleared
            return thunk.rejectWithValue({
                error: result.error,
                errorCode: result.errorCode,
            })
        }

        return result
    } catch (error) {
        // Clear tokens even on error
        membershipServices.clearTokens()
        return thunk.rejectWithValue({
            error: error.message,
            errorCode: null,
        })
    }
})

/**
 * Get current member data using JWT token
 */
export const getCurrentMemberAsync = createAsyncThunk('membership/getCurrentMember', async (_, thunk) => {
    try {
        const result = await membershipServices.getCurrentMember()
        if (!result.success) {
            return thunk.rejectWithValue(result.error)
        }
        return result
    } catch (error) {
        return thunk.rejectWithValue(error.message)
    }
})

/**
 * Retrieve member order history
 * @param {Object} input - order history request data
 * @param {number} input.limit - number of items to retrieve
 * @param {number} input.offset - number of items to skip
 */
export const retrieveMemberOrderHistoryAsync = createAsyncThunk('membership/retrieveMemberOrderHistory', async (input, thunk) => {
    try {
        const result = await membershipServices.retrieveMemberOrderHistory(input)
        if (!result.success) {
            return thunk.rejectWithValue(result.error)
        }
        return result
    } catch (error) {
        return thunk.rejectWithValue(error.message)
    }
})

/**
 * Retrieve member upcoming orders
 * @param {Object} input - upcoming orders request data
 * @param {number} input.limit - number of items to retrieve
 * @param {number} input.offset - number of items to skip
 */
export const retrieveMemberUpcomingOrdersAsync = createAsyncThunk(
    'membership/retrieveMemberUpcomingOrders',
    async (input, thunk) => {
        try {
            const result = await membershipServices.retrieveMemberUpcomingOrders(input)
            if (!result.success) {
                return thunk.rejectWithValue(result.error)
            }
            return result
        } catch (error) {
            return thunk.rejectWithValue(error.message)
        }
    }
)

/**
 * Check pending ratings
 */
export const checkPendingRatingsAsync = createAsyncThunk('membership/checkPendingRatings', async (_, thunk) => {
    try {
        const result = await membershipServices.checkPendingRatings()
        if (!result.success) {
            return thunk.rejectWithValue(result.error)
        }
        return result
    } catch (error) {
        return thunk.rejectWithValue(error.message)
    }
})

/**
 * Submit dish ratings
 * @param {Object} input - ratings data
 * @param {Array} input.ratings - array of dish ratings
 */
export const submitDishRatingsAsync = createAsyncThunk('membership/submitDishRatings', async (input, thunk) => {
    try {
        const result = await membershipServices.submitDishRatings(input)
        if (!result.success) {
            return thunk.rejectWithValue(result.error)
        }
        return result
    } catch (error) {
        return thunk.rejectWithValue(error.message)
    }
})

/**
 * Cancel member order
 * @param {Object} input - cancel order data
 * @param {number} input.orderId - order ID
 * @param {string} input.reason - optional cancellation reason
 */
export const cancelMemberOrderAsync = createAsyncThunk('membership/cancelMemberOrder', async (input, thunk) => {
    try {
        const result = await membershipServices.cancelMemberOrder(input)
        if (!result.success) {
            return thunk.rejectWithValue(result.error)
        }
        return result
    } catch (error) {
        return thunk.rejectWithValue(error.message)
    }
})
